// utils/crypto.js
import { TextDecoder } from "util";

let crypto = window.crypto;

/**
 * Converts a hex string to a Uint8Array.
 */
function hexToUint8Array(hexString) {
  if (!hexString || typeof hexString !== "string") {
    console.error("Invalid hex string provided");
    return;
  }
  const bytes = hexString.match(/.{1,2}/g)?.map((byte) => parseInt(byte, 16));
  if (!bytes || bytes.some(isNaN)) {
    console.error("Hex string contains invalid characters");
    return;
  }
  return new Uint8Array(bytes);
}

/**
 * Converts a Base64-encoded string to a Uint8Array.
 */
function base64ToUint8Array(base64String) {
  if (!base64String || typeof base64String !== "string") {
    console.error("Invalid Base64 string provided");
    return;
  }

  let binaryString = atob(base64String);
  return Uint8Array.from(
    binaryString.split("").map((char) => char.charCodeAt(0)),
  );
}

/**
 * Imports a raw key for AES-GCM decryption.
 */
async function importAesKey(keyBytes) {
  if (!(keyBytes instanceof Uint8Array) || keyBytes.length !== 32) {
    console.error("Key must be a 32-byte Uint8Array for AES-256");
    return;
  }
  return await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["decrypt"],
  );
}

/**
 * Decrypts data encrypted with AES-256-GCM.
 * @param {Object} encrypted - Encrypted data with properties 'data' (hex) and 'nonce' (hex).
 * @param config
 * @returns {Promise<string>} - The decrypted plaintext.
 */
export async function decryptAesGcm(encrypted, config) {
  try {
    if (!encrypted || !encrypted.data || !encrypted.nonce)
      throw new Error(
        'Encrypted object must contain "data" and "nonce" properties',
      );
    const secretKey = config.aesSecretKey;
    if (!secretKey) throw new Error("Secret key is required");

    const encryptedData = hexToUint8Array(encrypted.data);
    const nonceBytes = hexToUint8Array(encrypted.nonce);
    const keyBytes = base64ToUint8Array(secretKey);
    const cryptoKey = await importAesKey(keyBytes);
    const decrypted = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: nonceBytes },
      cryptoKey,
      encryptedData,
    );
    return new (TextDecoder ?? window.TextDecoder)().decode(decrypted);
  } catch (error) {
    console.error(`Decryption failed: ${error.message}`);
    throw error;
  }
}

/**
 * Convenience function for decrypting user data from auth
 */
export async function decryptUserData(user_data, nonce) {
  const encrypted = { data: user_data, nonce };
  return await decryptAesGcm(encrypted);
}
